import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { brokers, type Broker, type BrokerInput } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { baixarCsv } from '@/shared/lib/exportar';
import {
  formatCivilDate,
  formatDate,
  formatDocument,
  formatRate,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Icon } from '@/shared/ui/Icon';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';
import { buttonClassName } from '@/shared/ui/buttonStyles';

import { BlockBrokerDialog } from './BlockBrokerDialog';
import { BrokerDrawer } from './BrokerDrawer';
import { BrokerForm } from './BrokerForm';
import { BrokerKanban } from './BrokerKanban';
import { PERMISSAO_EXPORTAR, rotuloDaSituacaoDoCnpj } from './rotulos';
import { PartyToolbar } from '../shared/PartyToolbar';

const etapas = [
  { value: '', label: 'Todas as etapas' },
  { value: 'novo', label: 'Novo' },
  { value: 'em_documentacao', label: 'Em documentacao' },
  { value: 'em_validacao', label: 'Em validacao' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

const limite = 25;

/** Os filtros do dialogo "Corretores" do OnPoint: nome e CNPJ do corretor. */
interface FiltrosDoOnPoint {
  name: string;
  document: string;
}

const semFiltros: FiltrosDoOnPoint = { name: '', document: '' };

export function BrokersPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [busca, setBusca] = useState('');
  const [etapa, setEtapa] = useState('');
  const [offset, setOffset] = useState(0);
  const [filtros, setFiltros] = useState<FiltrosDoOnPoint>(semFiltros);
  const [filtrando, setFiltrando] = useState(false);
  const [emEdicao, setEmEdicao] = useState<Broker | null>(null);
  const [emDetalhe, setEmDetalhe] = useState<Broker | null>(null);
  const [excluindo, setExcluindo] = useState<Broker | null>(null);
  const [bloqueando, setBloqueando] = useState<Broker | null>(null);
  const [criando, setCriando] = useState(false);
  const [exportando, setExportando] = useState(false);

  // A visao escolhida acompanha a pessoa entre sessoes. Quem trabalha no quadro
  // trabalha no quadro todo dia, e reabrir sempre na tabela seria um passo a mais
  // toda vez, sem motivo.
  const [visao, setVisao] = useState<'lista' | 'quadro'>(() => {
    try {
      return localStorage.getItem('corretores.visao') === 'quadro' ? 'quadro' : 'lista';
    } catch {
      // Navegador que bloqueia armazenamento nao pode impedir a tela de abrir.
      return 'lista';
    }
  });

  function escolherVisao(escolhida: 'lista' | 'quadro') {
    setVisao(escolhida);
    try {
      localStorage.setItem('corretores.visao', escolhida);
    } catch {
      // A preferencia e conveniencia, e nao pode virar erro.
    }
  }

  const podeEscrever = can('corretores.corretor.create');
  const podeAtualizar = can('corretores.corretor.update');
  const podeExcluir = can('corretores.corretor.delete');
  const podeExportar = can(PERMISSAO_EXPORTAR);

  const parametros = {
    q: busca,
    name: filtros.name,
    document: filtros.document,
  };

  // O quadro mostra a esteira inteira: a lista vem em paginas de 100 ate o fim. Pedir
  // 200 de uma vez recebia 100 e escondia o resto (defeito 4 de "Corretora,
  // tomador..." do anexo C do 47).
  const consulta = useQuery({
    queryKey: ['brokers', { busca, etapa, offset, visao, filtros }],
    queryFn: ({ signal }) =>
      visao === 'quadro'
        ? brokers.listAll(parametros, signal)
        : brokers.list({ ...parametros, stage: etapa, limit: limite, offset }, signal),
  });

  const encontrados = consulta.data?.total ?? 0;
  const comFiltro = Boolean(busca || etapa || filtros.name || filtros.document);

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['brokers'] });
    void queryClient.invalidateQueries({ queryKey: ['broker'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const criar = useMutation({
    mutationFn: (input: BrokerInput) => brokers.create(input),
    onSuccess: (broker) => {
      toast.success(`Corretor ${broker.legal_name} cadastrado.`);
      setCriando(false);
      invalidar();
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: BrokerInput }) =>
      brokers.update(id, input),
    onSuccess: (broker) => {
      toast.success('Cadastro atualizado.');
      setEmEdicao(null);
      setEmDetalhe(broker);
      invalidar();
    },
  });

  const excluir = useMutation({
    mutationFn: (id: string) => brokers.remove(id),
    onSuccess: () => {
      toast.success('Corretor excluido.');
      setExcluindo(null);
      setEmDetalhe(null);
      invalidar();
    },
    // A recusa por vinculo e informacao util, e nao falha: ela diz que existe
    // historico, e o caminho passa a ser o bloqueio.
    onError: (erro: Error) => toast.error(erro.message),
  });

  const mover = useMutation({
    mutationFn: ({
      id,
      stage,
      justificativa,
    }: {
      id: string;
      stage: string;
      justificativa?: string;
    }) =>
      justificativa === undefined
        ? brokers.transition(id, stage)
        : brokers.transition(id, stage, justificativa),
    onSuccess: (broker) => {
      toast.success(
        `Corretor movido para ${statusLabel('corretor', broker.stage).toLowerCase()}.`,
      );
      setBloqueando(null);
      setEmDetalhe((atual) => (atual && atual.id === broker.id ? broker : atual));
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // O bloqueio pede a justificativa antes de ir ao servidor.
  function moverPara(corretor: Broker, destino: string) {
    if (destino === 'bloqueado') {
      setBloqueando(corretor);
      return;
    }
    mover.mutate({ id: corretor.id, stage: destino });
  }

  async function exportar() {
    setExportando(true);
    try {
      const itens = await brokers.export({ ...parametros, stage: etapa });
      baixarCsv('corretores', itens, [
        { titulo: 'Numero', valor: (item) => item.number },
        { titulo: 'CNPJ', valor: (item) => formatDocument(item.document) },
        { titulo: 'Nome', valor: (item) => item.legal_name },
        { titulo: 'Nome fantasia', valor: (item) => item.trade_name },
        { titulo: 'Comissao', valor: (item) => formatRate(item.commission_bps) },
        {
          titulo: 'Limite de comissao',
          valor: (item) =>
            item.max_commission_bps === null ? '' : formatRate(item.max_commission_bps),
        },
        { titulo: 'Data de cadastro', valor: (item) => formatDate(item.created_at) },
        {
          titulo: 'Status SUSEP',
          valor: (item) => statusLabel('situacaoExterna', item.external_status),
        },
        { titulo: 'Etapa', valor: (item) => statusLabel('corretor', item.stage) },
        { titulo: 'Bloqueado', valor: (item) => (item.is_blocked ? 'Sim' : 'Nao') },
        { titulo: 'Data bloqueio', valor: (item) => formatDate(item.blocked_at) },
        { titulo: 'Codigo SUSEP', valor: (item) => item.susep_code },
        {
          titulo: 'Situacao do CNPJ',
          valor: (item) => rotuloDaSituacaoDoCnpj(item.cnpj_status),
        },
        { titulo: 'Codigo CNAE', valor: (item) => item.cnae_code },
        { titulo: 'Canal', valor: (item) => item.channel_name },
        { titulo: 'Level corretor', valor: (item) => item.level },
        {
          titulo: 'Data expiracao do cadastro',
          valor: (item) => formatCivilDate(item.registration_expires_on),
        },
        { titulo: 'Email', valor: (item) => item.email },
        { titulo: 'Telefone', valor: (item) => item.phone },
        { titulo: 'Cidade', valor: (item) => item.city },
        { titulo: 'UF', valor: (item) => item.state },
      ]);
      toast.success(
        `${itens.length} ${itens.length === 1 ? 'corretor exportado' : 'corretores exportados'}.`,
      );
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Nao foi possivel exportar.');
    } finally {
      setExportando(false);
    }
  }

  // As colunas da lista do OnPoint, na ordem dele (Acoes, CNPJ, Nome, Comissao, Data
  // de cadastro, Status SUSEP, Bloqueado, Data bloqueio), seguidas das que o
  // SeguraCore ja tinha (etapa e numero). A tabela mostra as Acoes por ultimo, como em
  // toda a plataforma (DataTable).
  const colunas: Column<Broker>[] = [
    {
      key: 'acoes',
      header: 'Acoes',
      render: (broker) => (
        <div className="flex gap-1" onClick={(event) => event.stopPropagation()}>
          <Link
            to={`/corretores/${broker.id}`}
            className={buttonClassName({
              variant: 'secondary',
              size: 'sm',
              className: 'whitespace-nowrap',
            })}
          >
            Abrir cadastro
          </Link>
        </div>
      ),
    },
    {
      key: 'cnpj',
      header: 'CNPJ',
      render: (broker) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatDocument(broker.document)}
        </span>
      ),
    },
    {
      key: 'nome',
      header: 'Nome',
      render: (broker) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{broker.legal_name}</p>
          {broker.trade_name ? (
            <p className="truncate text-2xs text-ink-500">{broker.trade_name}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'comissao',
      header: 'Comissao',
      align: 'right',
      hideBelow: 'sm',
      render: (broker) => formatRate(broker.commission_bps),
    },
    {
      key: 'cadastro',
      header: 'Data de cadastro',
      hideBelow: 'md',
      render: (broker) => formatDate(broker.created_at),
    },
    {
      key: 'externa',
      header: 'Status SUSEP',
      hideBelow: 'lg',
      render: (broker) => (
        <StatusBadge kind="situacaoExterna" value={broker.external_status} />
      ),
    },
    {
      key: 'bloqueado',
      header: 'Bloqueado',
      render: (broker) =>
        broker.stage === 'bloqueado' ? (
          <Badge tone="danger">Sim</Badge>
        ) : (
          <Badge tone="neutral">Nao</Badge>
        ),
    },
    {
      key: 'data-bloqueio',
      header: 'Data bloqueio',
      hideBelow: 'md',
      render: (broker) => (broker.blocked_at ? formatDate(broker.blocked_at) : ''),
    },
    {
      key: 'etapa',
      header: 'Etapa',
      render: (broker) => <StatusBadge kind="corretor" value={broker.stage} />,
    },
    {
      key: 'numero',
      header: 'Numero',
      hideBelow: 'lg',
      render: (broker) => <span className="tabular-nums">{broker.number}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Corretores"
        description="Lista de corretores, a esteira de ativacao e o cadastro completo de cada corretora."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div
              role="group"
              aria-label="Forma de visualizacao"
              className="flex rounded-md border border-line p-0.5"
            >
              <button
                type="button"
                aria-pressed={visao === 'lista'}
                onClick={() => escolherVisao('lista')}
                className={
                  visao === 'lista'
                    ? 'rounded px-2.5 py-1 text-xs font-medium bg-ink-100 text-ink-900'
                    : 'rounded px-2.5 py-1 text-xs text-ink-500 hover:text-ink-900'
                }
              >
                Lista
              </button>
              <button
                type="button"
                aria-pressed={visao === 'quadro'}
                onClick={() => escolherVisao('quadro')}
                className={
                  visao === 'quadro'
                    ? 'rounded px-2.5 py-1 text-xs font-medium bg-ink-100 text-ink-900'
                    : 'rounded px-2.5 py-1 text-xs text-ink-500 hover:text-ink-900'
                }
              >
                Quadro
              </button>
            </div>

            <Button variant="secondary" onClick={() => setFiltrando(true)}>
              Filtros
            </Button>

            {podeExportar ? (
              <Button
                variant="secondary"
                loading={exportando}
                onClick={() => void exportar()}
              >
                Exportar dados
              </Button>
            ) : null}

            {podeEscrever ? (
              // O cadastro simples, num formulario so, continua como atalho: ele tem
              // a comissao, o Pix, os produtos e as observacoes, que o assistente do
              // OnPoint nao pede (P-COR-2).
              <Button variant="secondary" onClick={() => setCriando(true)}>
                Cadastro simples
              </Button>
            ) : null}

            {podeEscrever ? (
              <Link
                to="/corretores/novo"
                className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-500 px-4 text-sm font-medium text-white shadow-(--shadow-brand) hover:bg-brand-600"
              >
                <Icon name="users" size={15} />
                Cadastrar novo corretor
              </Link>
            ) : null}
          </div>
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os corretores">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por razao social, nome fantasia ou CNPJ"
          filter={
            visao === 'lista' ? (
              <Select
                label="Etapa"
                className="h-9"
                options={etapas}
                value={etapa}
                onChange={(event) => {
                  setEtapa(event.target.value);
                  setOffset(0);
                }}
              />
            ) : undefined
          }
          extra={
            <span className="self-end text-xs text-ink-600">
              {encontrados} {encontrados === 1 ? 'corretor' : 'corretores'}
            </span>
          }
        />

        {visao === 'quadro' ? (
          <div className="p-3">
            <BrokerKanban
              corretores={consulta.data?.items ?? []}
              carregando={consulta.isPending}
              podeMover={podeAtualizar}
              onMover={moverPara}
              onAbrir={(corretor) => setEmDetalhe(corretor)}
            />
          </div>
        ) : (
          <DataTable
            columns={colunas}
            items={consulta.data?.items ?? []}
            keyOf={(broker) => broker.id}
            loading={consulta.isPending}
            onRowClick={(broker) => setEmDetalhe(broker)}
            empty={
              <EmptyState
                icon="users"
                title={
                  comFiltro
                    ? 'Nenhum corretor encontrado'
                    : 'Nenhum corretor cadastrado'
                }
                description={
                  comFiltro
                    ? 'Ajuste a busca, os filtros ou o filtro de etapa.'
                    : 'Cadastre a primeira corretora parceira para comecar a esteira de ativacao.'
                }
                action={
                  podeEscrever && !comFiltro ? (
                    <Button onClick={() => void navigate('/corretores/novo')}>
                      Cadastrar corretor
                    </Button>
                  ) : null
                }
              />
            }
          />
        )}

        {visao === 'lista' ? (
          <Pagination
            total={encontrados}
            limit={limite}
            offset={offset}
            onChange={setOffset}
          />
        ) : null}
      </Card>

      {filtrando ? (
        <FiltrosDialog
          atuais={filtros}
          onClose={() => setFiltrando(false)}
          onAplicar={(novos) => {
            setFiltros(novos);
            setOffset(0);
            setFiltrando(false);
          }}
        />
      ) : null}

      <Modal
        open={criando}
        onClose={() => setCriando(false)}
        title="Cadastro simples de corretor"
        description="Num formulario so, sem os convites. O corretor nasce na primeira etapa da esteira de ativacao."
      >
        <BrokerForm
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      </Modal>

      {emDetalhe ? (
        <BrokerDrawer
          corretor={emDetalhe}
          podeAtualizar={podeAtualizar}
          podeExcluir={podeExcluir}
          movendo={mover.isPending}
          onMover={(destino) => moverPara(emDetalhe, destino)}
          onEditar={() => {
            setEmEdicao(emDetalhe);
            setEmDetalhe(null);
          }}
          onExcluir={() => setExcluindo(emDetalhe)}
          onClose={() => setEmDetalhe(null)}
        />
      ) : null}

      <BlockBrokerDialog
        open={bloqueando !== null}
        nome={bloqueando?.legal_name ?? ''}
        enviando={mover.isPending}
        onClose={() => setBloqueando(null)}
        onConfirmar={(justificativa) =>
          bloqueando &&
          mover.mutate({ id: bloqueando.id, stage: 'bloqueado', justificativa })
        }
      />

      {excluindo ? (
        <ConfirmDialog
          open
          onClose={() => setExcluindo(null)}
          title={`Excluir ${excluindo.legal_name}`}
          message="A exclusao so acontece quando o corretor nunca entrou em um negocio. Se ele ja intermediou alguma operacao, o cadastro permanece e o caminho e bloquea-lo na esteira."
          confirmLabel="Excluir"
          tone="danger"
          loading={excluir.isPending}
          onConfirm={() => excluir.mutate(excluindo.id)}
        />
      ) : null}

      {emEdicao ? (
        <Modal
          open
          onClose={() => setEmEdicao(null)}
          title={emEdicao.legal_name}
          description={`Corretor ${emEdicao.number}`}
          size="lg"
        >
          <BrokerForm
            broker={emEdicao}
            readOnly={!podeAtualizar}
            submitting={atualizar.isPending}
            error={atualizar.error}
            onCancel={() => setEmEdicao(null)}
            onSubmit={(input) => atualizar.mutate({ id: emEdicao.id, input })}
          />
        </Modal>
      ) : null}
    </div>
  );
}

/** O dialogo "Corretores" do OnPoint, com Limpar filtro e Aplicar filtro. */
function FiltrosDialog({
  atuais,
  onClose,
  onAplicar,
}: {
  atuais: FiltrosDoOnPoint;
  onClose: () => void;
  onAplicar: (filtros: FiltrosDoOnPoint) => void;
}) {
  const [nome, setNome] = useState(atuais.name);
  const [cnpj, setCnpj] = useState(atuais.document);
  return (
    <Modal
      open
      onClose={onClose}
      title="Corretores"
      description="Filtre a lista pelo nome ou pelo CNPJ do corretor."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onAplicar(semFiltros)}>
            Limpar filtro
          </Button>
          <Button
            onClick={() => onAplicar({ name: nome.trim(), document: cnpj.trim() })}
          >
            Aplicar filtro
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Nome do Corretor"
          placeholder="Digite o nome"
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
        />
        <Field
          label="CNPJ Corretor"
          placeholder="Digite o CNPJ"
          value={cnpj}
          onChange={(evento) => setCnpj(evento.target.value)}
        />
      </div>
    </Modal>
  );
}
